import { LitElement, html } from 'lit-element'
import { t } from '../../directives/translate'
import { unsafeSVG } from 'lit-html/directives/unsafe-svg'
import { SVG_EXTERNAL_LINK_OUTLINED } from '@getgo/chameleon-icons'
import { openNewTabFor } from '../../common/dom-helpers'
import { environment } from '../../environments'
import { getShellAnalytics } from '../../common/shell-api-helpers'
import { asTranslationKey } from '../../common/translate-helpers/i18n-utils'
import { repeat } from 'lit-html/directives/repeat'
import { nothing } from 'lit-html'
import { setupUserReport } from '../user-report'
import { isGoToMeetingCustomer } from '../../services/identity/entitlement'

export interface MenuItem {
  readonly labelKey: string
  readonly dataTest: string
  readonly executeFunction: () => void
  readonly isExternal: () => boolean
}

export const externalMenuItems: readonly MenuItem[] = [
  {
    executeFunction: () => {
      openNewTabFor(environment().helpUrl)
    },
    labelKey: asTranslationKey('Get help'),
    dataTest: 'get-help',
    isExternal: () => true,
  },
  {
    executeFunction: () => {
      openNewTabFor(environment().tutorialsAndVideosUrl)
    },
    labelKey: asTranslationKey('Tutorials and videos'),
    dataTest: 'tutorials-and-videos',
    isExternal: () => true,
  },
  {
    executeFunction: () => {
      openNewTabFor(environment().communityForumUrl)
    },
    labelKey: asTranslationKey('Community forum'),
    dataTest: 'community-forum',
    isExternal: () => true,
  },
]

export const feedbackMenuItem: MenuItem = {
  executeFunction: () => {
    if (isGoToMeetingCustomer()) {
      window.open('https://logmeinvoc.iad1.qualtrics.com/jfe/form/SV_39obCEnQHAKSR7g', '_blank', 'noreferrer')
    } else {
      const { openFeedbackDialog } = setupUserReport(window)
      openFeedbackDialog()
    }
  },
  labelKey: asTranslationKey('Give feedback'),
  dataTest: 'feedback',
  isExternal: () => isGoToMeetingCustomer(),
}

export class GoToTopbarHelpMenu extends LitElement {
  static readonly tagName = 'goto-topbar-help-menu'

  private renderMenuItem(menuItem: MenuItem) {
    const { labelKey, executeFunction, dataTest } = menuItem
    return html`
      <chameleon-menu-item
        @click=${() => {
          getShellAnalytics().track('GoTo > Help menu', { action: 'Click', target: labelKey, from: 'Topbar' })
          executeFunction()
        }}
        aria-label=${t(labelKey)}
        data-test=${dataTest}
      >
        ${t(labelKey)} ${this.createEndSlot(menuItem.isExternal)}
      </chameleon-menu-item>
    `
  }

  private createEndSlot = (isExternalLink: () => boolean) =>
    isExternalLink()
      ? html` <chameleon-svg slot="end">${unsafeSVG(SVG_EXTERNAL_LINK_OUTLINED)}</chameleon-svg>`
      : nothing

  render() {
    return html`
      <chameleon-menu label=${t('Help menu')}>
        <chameleon-menu-section>${t('Help')}</chameleon-menu-section>
        ${repeat(
          externalMenuItems,
          menuItem => menuItem.dataTest,
          menuItem => this.renderMenuItem(menuItem),
        )}
        <chameleon-menu-separator></chameleon-menu-separator>
        ${this.renderMenuItem(feedbackMenuItem)}
      </chameleon-menu>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    readonly [GoToTopbarHelpMenu.tagName]: GoToTopbarHelpMenu
  }
}
